import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react"

interface UseControllableOpenStateOptions {
    isOpen?: boolean
    defaultOpen?: boolean
    onOpenChange?: (open: boolean) => void
}

interface UseControllableOpenStateReturn {
    isControlled: boolean
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
}

export default function useControllableOpenState(
    options: UseControllableOpenStateOptions,
): UseControllableOpenStateReturn {
    const { isOpen: controlledOpen, defaultOpen = false, onOpenChange } = options
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
    const isControlled = typeof controlledOpen === "boolean"
    const isOpen = isControlled ? controlledOpen : uncontrolledOpen
    const onOpenChangeRef = useRef(onOpenChange)
    const isOpenRef = useRef(isOpen)

    useEffect(() => {
        onOpenChangeRef.current = onOpenChange
    }, [onOpenChange])

    useEffect(() => {
        isOpenRef.current = isOpen
    }, [isOpen])

    const setIsOpen = useCallback<Dispatch<SetStateAction<boolean>>>(
        (nextOpen) => {
            const resolvedOpen = typeof nextOpen === "function" ? nextOpen(isOpenRef.current) : nextOpen

            if (!isControlled) {
                setUncontrolledOpen(resolvedOpen)
            }

            onOpenChangeRef.current?.(resolvedOpen)
        },
        [isControlled],
    )

    return {
        isControlled,
        isOpen,
        setIsOpen,
    }
}
